//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import React, { useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Button, Dropdown, DropdownProps, Icon, Label, Menu, Message, Modal, Popup, Table } from "semantic-ui-react";
import { DeploymentNotificationSummary } from "../../api/data/v1/idata";
import { NotificationSeverity } from "../../api/lib";
import { OrganizationNotificationSummary } from "../../api/resourcemanager/v1/iresourcemanager";
import { Confirm, ConfirmInfo, FormActionButtonCancel } from "../../ui/lib";
import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";
import { IWithRefreshProps } from "../../util/WithRefresh";
import DeploymentList from "../deployment/DeploymentList";
import OrganizationList from "../organization/OrganizationList";
import { builtInNotifications } from "./builtInNotifications";
import { SeverityLabel } from "./severity";
import {
  ActiveNotification,
  ActiveNotificationSource,
  NotificationRule,
  NotificationRuleTarget,
  NotificationSummary,
  sourceLabels,
  targetLabels,
  toDate,
  toSeverity,
} from "./types";

const sourceColor = (source: ActiveNotificationSource) => {
  switch (source) {
    case "rule":
      return "teal";
    case "built-in":
      return "grey";
    default:
      return "purple";
  }
};

// mergeSummaries merges the per-service notification summaries by notification ID and classifies each ID:
// "rule" if a loaded rule sets it, "built-in" if an inspector sets it in code, "manual" otherwise.
export const mergeSummaries = (
  deployments: DeploymentNotificationSummary[],
  organizations: OrganizationNotificationSummary[],
  rules: NotificationRule[]
): ActiveNotification[] => {
  const merged = new Map<string, ActiveNotification>();
  const add = (kind: NotificationRuleTarget, s: NotificationSummary) => {
    const id = s.notification_id || "";
    const oldest = toDate(s.oldest_created_at);
    const newest = toDate(s.newest_created_at);
    const current = merged.get(id);
    const item: ActiveNotification = current || {
      notification_id: id,
      source: "manual",
      severity: toSeverity(s.severity),
      text: s.notification_text || "",
      deployments: 0,
      deleted_deployments: 0,
      organizations: 0,
      deleted_organizations: 0,
      oldest_created_at: oldest,
      newest_created_at: newest,
    };
    if (kind === "deployment") {
      item.deployments += s.count || 0;
      item.deleted_deployments += s.deleted_count || 0;
    } else {
      item.organizations += s.count || 0;
      item.deleted_organizations += s.deleted_count || 0;
    }
    if (current) {
      if (oldest && (!item.oldest_created_at || oldest < item.oldest_created_at)) item.oldest_created_at = oldest;
      if (newest && (!item.newest_created_at || newest > item.newest_created_at)) {
        // Text and severity of the newest occurrence.
        item.newest_created_at = newest;
        item.text = s.notification_text || "";
        item.severity = toSeverity(s.severity);
      }
    }
    merged.set(id, item);
  };
  deployments.forEach((s) => add("deployment", s));
  organizations.forEach((s) => add("organization", s));
  return Array.from(merged.values())
    .map((item) => {
      const ruleIds = rules.filter((r) => r.notification.notificationId === item.notification_id).map((r) => r.id);
      if (ruleIds.length) return { ...item, source: "rule" as ActiveNotificationSource, source_ref: ruleIds.join(", ") };
      const builtIn = builtInNotifications[item.notification_id];
      if (builtIn) return { ...item, source: "built-in" as ActiveNotificationSource, source_ref: builtIn.source_ref, condition: builtIn.condition };
      return item;
    })
    .sort((a, b) => a.notification_id.localeCompare(b.notification_id));
};

// countText renders a count with the deleted resources in brackets, e.g. "12 (+1 deleted)".
const countText = (active: number, deleted: number) => (deleted ? `${active} (+${deleted} deleted)` : `${active || "-"}`);
const deploymentsText = (item: ActiveNotification) => countText(item.deployments, item.deleted_deployments);
const organizationsText = (item: ActiveNotification) => countText(item.organizations, item.deleted_organizations);

// AffectedModal lists the deployments / organizations currently carrying the notification.
// Only kinds listed in kinds are shown (those the caller may list).
const AffectedModal = ({
  item,
  kinds,
  onClose,
  ...props
}: { item: ActiveNotification; kinds: NotificationRuleTarget[]; onClose: () => void } & IWithRefreshProps & RouteComponentProps) => {
  const [kind, setKind] = useState<NotificationRuleTarget>(kinds[0] || "deployment");
  const filter = { notification_id: item.notification_id };
  // Deployments and organizations are soft-deleted; the lists start with the non-deleted ones, matching the inventory counts.
  const deploymentFilter = { ...filter, is_not_deleting: true };
  const organizationFilter = { ...filter, is_not_deleted: true };
  return (
    <Modal open size="fullscreen">
      <Modal.Header>
        Resources carrying <code>{item.notification_id}</code>
      </Modal.Header>
      <Modal.Content scrolling>
        {kinds.length > 1 && (
          <Menu pointing secondary>
            {kinds.map((k) => (
              <Menu.Item key={k} active={kind === k} onClick={() => setKind(k)}>
                {targetLabels[k]} ({k === "deployment" ? deploymentsText(item) : organizationsText(item)})
              </Menu.Item>
            ))}
          </Menu>
        )}
        {kind === "deployment" ? (
          <>
            <Message info size="small">
              <b>{item.deployments}</b> active and <b>{item.deleted_deployments}</b> deleted deployment(s) carry this notification. Use the Status filter
              (Deleting) to show deleted deployments.
            </Message>
            <DeploymentList
              {...props}
              filterView
              selectedFilters={deploymentFilter}
              onDeploymentSelected={(id: string): void => {}}
              onOrganizationSelected={(id: string): void => {}}
              onProjectSelected={(id: string): void => {}}
              onDataClusterSelected={(id: string): void => {}}
            />
          </>
        ) : (
          <>
            <Message info size="small">
              <b>{item.organizations}</b> active and <b>{item.deleted_organizations}</b> deleted organization(s) carry this notification. Use the Deleted filter
              to show deleted organizations.
            </Message>
            <OrganizationList {...props} filterView selectedFilters={organizationFilter} onOrganizationSelected={(id: string): void => {}} />
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <FormActionButtonCancel title="Close" onClick={onClose} />
      </Modal.Actions>
    </Modal>
  );
};

// ActiveNotificationsView is the inventory of every notification ID currently present on deployments
// and organizations, whatever created it (rule, built-in inspector, manual).
export const ActiveNotificationsView = ({
  items,
  canRemoveEverywhere,
  canListRules,
  canShowAffected,
  rulesError,
  onRemoveEverywhere,
  ...props
}: {
  items: ActiveNotification[];
  canRemoveEverywhere: Record<NotificationRuleTarget, boolean>;
  // Without the rule-list permission of a kind its rules are not loaded, so an ID cannot be proven manual.
  canListRules: Record<NotificationRuleTarget, boolean>;
  // "Show affected" opens the deployment / organization list, which needs that list's list + count permissions.
  canShowAffected: Record<NotificationRuleTarget, boolean>;
  // Targets whose rules failed to load: a rule-owned ID of that kind would look manual, so removal is hidden.
  rulesError: Partial<Record<NotificationRuleTarget, string>>;
  onRemoveEverywhere: (notificationId: string, kinds: NotificationRuleTarget[]) => void;
} & IWithRefreshProps &
  RouteComponentProps) => {
  const [affected, setAffected] = useState<ActiveNotification | undefined>(undefined);
  const [confirmInfo, setConfirmInfo] = useState<ConfirmInfo | undefined>(undefined);
  const [sourceFilter, setSourceFilter] = useState<ActiveNotificationSource | "all">("all");
  const [targetFilter, setTargetFilter] = useState<NotificationRuleTarget | "all">("all");
  const [severityFilter, setSeverityFilter] = useState<NotificationSeverity | "all">("all");

  // kindsOf lists the kinds with non-deleted resources carrying the notification ("Remove everywhere" skips deleted ones).
  const kindsOf = (item: ActiveNotification): NotificationRuleTarget[] => [
    ...(item.deployments > 0 ? ["deployment" as NotificationRuleTarget] : []),
    ...(item.organizations > 0 ? ["organization" as NotificationRuleTarget] : []),
  ];
  // carryingKindsOf also counts deleted deployments / organizations ("Show affected" can list them).
  const carryingKindsOf = (item: ActiveNotification): NotificationRuleTarget[] => [
    ...(item.deployments > 0 || item.deleted_deployments > 0 ? ["deployment" as NotificationRuleTarget] : []),
    ...(item.organizations > 0 || item.deleted_organizations > 0 ? ["organization" as NotificationRuleTarget] : []),
  ];
  const affectedKinds = (item: ActiveNotification) => carryingKindsOf(item).filter((kind) => canShowAffected[kind]);
  // "Remove everywhere" needs the remove-notification-from-all permission for every kind that carries the notification,
  // and the rules of every such kind listable and loaded (otherwise the ID may belong to a rule).
  // It skips deleted resources, so it is hidden when only deleted ones carry the notification.
  const mayRemove = (item: ActiveNotification) =>
    item.source === "manual" && kindsOf(item).length > 0 && kindsOf(item).every((kind) => canRemoveEverywhere[kind] && canListRules[kind] && !rulesError[kind]);

  const handleRemove = (item: ActiveNotification) => {
    setConfirmInfo({
      header: `Remove ${item.notification_id} everywhere?`,
      warning: `The notification will be removed from ${item.deployments} deployment(s) and ${item.organizations} organization(s). Manual notifications are not recreated automatically.`,
      onConfirm: async () => {
        onRemoveEverywhere(item.notification_id, kindsOf(item));
        setConfirmInfo(undefined);
      },
      onDenied: () => setConfirmInfo(undefined),
    });
  };

  const sourceOptions = [
    { key: "all", value: "all", text: "Source: All" },
    ...(Object.keys(sourceLabels) as ActiveNotificationSource[]).map((key) => ({ key, value: key, text: `Source: ${sourceLabels[key]}` })),
  ];
  const targetOptions = [
    { key: "all", value: "all", text: "Target: All" },
    ...(Object.keys(targetLabels) as NotificationRuleTarget[]).map((key) => ({ key, value: key, text: `Target: ${targetLabels[key]}` })),
  ];
  const matchesTarget = (i: ActiveNotification) => targetFilter === "all" || carryingKindsOf(i).includes(targetFilter);
  const severityOptions = [
    { key: "all", value: "all", text: "Severity: All" },
    ...[
      NotificationSeverity.NOTIFICATION_SEVERITY_INFO,
      NotificationSeverity.NOTIFICATION_SEVERITY_WARNING,
      NotificationSeverity.NOTIFICATION_SEVERITY_CRITICAL,
    ].map((severity) => ({ key: severity, value: severity, text: `Severity: ${NotificationSeverity[severity].replace("NOTIFICATION_SEVERITY_", "")}` })),
  ];
  const visible = items.filter(
    (i) => (sourceFilter === "all" || i.source === sourceFilter) && matchesTarget(i) && (severityFilter === "all" || i.severity === severityFilter)
  );

  return (
    <>
      <Confirm confirmInfo={confirmInfo} />
      {affected && <AffectedModal {...props} item={affected} kinds={affectedKinds(affected)} onClose={() => setAffected(undefined)} />}
      <Dropdown
        selection
        options={sourceOptions}
        value={sourceFilter}
        onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => setSourceFilter(data.value as ActiveNotificationSource | "all")}
      />{" "}
      <Dropdown
        selection
        options={targetOptions}
        value={targetFilter}
        onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => setTargetFilter(data.value as NotificationRuleTarget | "all")}
      />{" "}
      <Dropdown
        selection
        options={severityOptions}
        value={severityFilter}
        onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => setSeverityFilter(data.value as NotificationSeverity | "all")}
      />
      <Table striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Notification ID</Table.HeaderCell>
            <Table.HeaderCell>Source</Table.HeaderCell>
            <Table.HeaderCell>Severity</Table.HeaderCell>
            <Table.HeaderCell>Text</Table.HeaderCell>
            <Table.HeaderCell>Deployments</Table.HeaderCell>
            <Table.HeaderCell>Organizations</Table.HeaderCell>
            <Table.HeaderCell>Oldest</Table.HeaderCell>
            <Table.HeaderCell>Newest</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {visible.map((item) => (
            <Table.Row key={item.notification_id}>
              <Table.Cell>
                <code>{item.notification_id}</code>
              </Table.Cell>
              <Table.Cell>
                <Label size="tiny" color={sourceColor(item.source)}>
                  {sourceLabels[item.source]}
                </Label>
                {item.source_ref && (
                  <>
                    <br />
                    <small>{item.source_ref}</small>
                  </>
                )}
                {item.condition && (
                  <>
                    <br />
                    <small>
                      <Icon name="code" /> {item.condition}
                    </small>
                  </>
                )}
              </Table.Cell>
              <Table.Cell>
                <SeverityLabel severity={item.severity} />
              </Table.Cell>
              <Table.Cell>
                <Popup wide="very" content={item.text} trigger={<span>{item.text.slice(0, 60)}&hellip;</span>} />
              </Table.Cell>
              <Table.Cell>{deploymentsText(item)}</Table.Cell>
              <Table.Cell>{organizationsText(item)}</Table.Cell>
              <Table.Cell>
                {item.oldest_created_at ? <DateTimePopupWithUTCAndLocalTime dateTime={item.oldest_created_at.toISOString()} label="Oldest" /> : "-"}
              </Table.Cell>
              <Table.Cell>
                {item.newest_created_at ? <DateTimePopupWithUTCAndLocalTime dateTime={item.newest_created_at.toISOString()} label="Newest" /> : "-"}
              </Table.Cell>
              <Table.Cell>
                {!!affectedKinds(item).length && <Button basic size="tiny" icon="eye" content="Show affected" onClick={() => setAffected(item)} />}
                {mayRemove(item) && <Button basic size="tiny" icon="trash" content="Remove everywhere" onClick={() => handleRemove(item)} />}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {!visible.length && <Message>No active notifications</Message>}
    </>
  );
};
